import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { auth } from "../../../firebaseConfig";
import app from "../../../firebaseConfig";
import CompanyLayout from "../../../components/layouts/CompanyLayout";
import CompanyProfile from "./CompanyProfile";
import AccountsOverview from "./AccountsOverview";
import CompanyStatusCard from "./CompanyStatusCard";
import SubscriptionInfoCard from "./SubscriptionInfoCard";

/**
 * CompanySettingsPage
 * Main settings page organizing company profile, accounts, and status
 * in a clean, structured layout with clear visual hierarchy.
 */
const CompanySettingsPage = ({ companyKey: companyKeyProp }) => {
  const firestore = getFirestore(app);
  const [companyKey, setCompanyKey] = useState(companyKeyProp || "");
  const [loading, setLoading] = useState(!companyKeyProp);

  // Resolve companyKey from prop or current signed-in user's Firestore users/{uid}.companyName
  useEffect(() => {
    if (companyKeyProp) {
      setCompanyKey(companyKeyProp);
      setLoading(false);
      return;
    }
    
    let isMounted = true;
    
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        if (isMounted) {
          setCompanyKey("");
          setLoading(false);
        }
        return;
      }
      try {
        const userRef = doc(firestore, "users", u.uid);
        const userSnap = await getDoc(userRef);
        const data = userSnap.exists() ? userSnap.data() : null;
        if (isMounted) {
          setCompanyKey(data?.companyName || "");
        }
      } catch (err) {
        console.error("resolve companyKey", err);
        if (isMounted) {
          setCompanyKey("");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    });
    
    return () => {
      isMounted = false;
      unsub();
    };
  }, [companyKeyProp, firestore]);

  if (loading) {
    return (
      <CompanyLayout>
        <div className="max-w-7xl mx-auto space-y-6 p-6">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">Loading company settings...</p>
          </div>
        </div>
      </CompanyLayout>
    );
  }

  if (!companyKey) {
    return (
      <CompanyLayout>
        <div className="max-w-7xl mx-auto space-y-6 p-6">
          <div className="text-center py-12">
            <p className="text-red-600">Unable to load company information. Please make sure you're logged in.</p>
          </div>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="max-w-7xl mx-auto space-y-6 p-6">
        {/* Page Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-teal-500 via-cyan-600 to-indigo-600">
            Company Settings
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Manage your company profile, accounts, and subscription status
          </p>
        </header>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Profile Card */}
            <section>
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Company Profile
              </h2>
              <CompanyProfile companyKey={companyKey} />
            </section>

            {/* Accounts Overview Card */}
            <section>
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Financial Accounts
              </h2>
              <div className="bg-white rounded-lg border shadow-sm p-6">
                <AccountsOverview companyKey={companyKey} />
              </div>
            </section>
          </div>

          {/* Right Column - Sidebar (1/3 width) */}
          <div className="space-y-6">
            {/* Company Status Card */}
            <section>
              <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Subscription Status
              </h2>
              <CompanyStatusCard companyKey={companyKey} />
            </section>

            {/* Subscription Info Card */}
            <section>
              <SubscriptionInfoCard companyKey={companyKey} />
            </section>
          </div>
        </div>
      </div>
    </CompanyLayout>
  );
};

export default CompanySettingsPage;